// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Grid, Typography, Switch, Button, Icon, IconButton } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../../common/components';
import { useDialog, useOnChange } from '../../../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../../../common/utils';
import ProductChangeForm from '../../../common/ProductChangeForm';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '1180px',
    overflow: 'hidden',
  },
  actions: {
    display: 'flex',
    '& > .MuiButton-root': {
      marginRight: theme.spacing(1),
      '&:last-child': {
        marginRight: '0',
      },
    },
  },
}));

function ProductChangesTab(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    product,
    productChanges,
    onCreateProductChange,
    onCreateProductChangeError,
    onDeleteProductChange,
    filterParam,
    sortParams,
    onFilterChange,
    onSortChange,
    onPageChange,
    actionsDisabled,
  } = props;

  const [sortModel, setSortModel] = useState([
    {
      field: sortParams.sortBy,
      sort: sortParams.sortOrder,
    },
  ]);

  const handleFilterChange = event => {
      onFilterChange(event.target.checked);
  };

  useOnChange(sortModel, () => {
    if (_.isEmpty(sortModel)) {
      return;
    }

    onSortChange({
      sortBy: sortModel[0].field,
      sortOrder: sortModel[0].sort,
    });
  });

  const handleSortModelChange = params => {
    if (!_.isEqual(params.sortModel, sortParams)) {
      setSortModel(params.sortModel);
    }
  };

  const generateChanges = row => {
    return row.productChangeLines
      .map(changeLine => `${changeLine.type} => 
        ${changeLine.type === 'PRICE' ? StringUtils.getFormattedSum(changeLine.value) : changeLine.value}`)
      .join(' | ');
  }

  const handleShowDeleteProductChangeDialog = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    event.stopPropagation();
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('product.productChangeDeleteConfirm')}</Typography>,
      onConfirm: (value) => onDeleteProductChange(id),
    });
  };

  const getColumnDefs = () => [
    {
      field: 'id',
      headerName: t('product.productChangeListId'),
      width: 155,
    },
    {
      field: 'effective_date',
      headerName: t('product.productChangeEffectiveDate'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.row.effectiveDate),
    },
    {
      field: 'client_id',
      headerName: t('product.productChangeClientId'),
      width: 175,
      valueFormatter: params => params.row.clientId === 1 ? 'Play' : params.row.clientId === 3 ? 'Kundeservice' : 'UNKNOWN',
    },
    {
      field: 'changes',
      headerName: t('product.productChanges'),
      width: 175,
      flex: 1,
      sortable: false,
      valueFormatter: params => generateChanges(params.row),
    },
    {
      field: 'state',
      headerName: t('product.productChangeState'),
      width: 155,
    },
    {
      field: 'creator',
      headerName: t('product.productChangeAuthor'),
      width: 155,
    },
    {
      field: 'created_at',
      headerName: t('product.productChangeCreated'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.row.createdAt),
    },
    {
      field: 'delete',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      type: 'boolean',
      renderHeader: () => <div>&nbsp;</div>,
      renderCell: (params) => {
        return (
          <>
            { 'PENDING' !== params.row.state ? '' : (
              <IconButton size="small" onClick={event => handleShowDeleteProductChangeDialog(event, params.row.id)}>
                <Icon color="error">delete_forever</Icon>
              </IconButton>
            )}
          </>
        );
      },
    },
  ];

  const getRowData = () => _.defaultTo(productChanges?.productChanges, []);

  const handleShowCreateChangeDialog = (productChange, edit = false) => {
    showDialog({
      title: t(`product.productChange${edit ? 'Create' : 'Details'}`),
      content: params => (
        <Box className={classes.dialogContent}>
          <ProductChangeForm
            product={product}
            productChange={productChange?.row}
            onSave={params.onConfirm}
            onError={onCreateProductChangeError}
            onCancel={params.onCancel}
            editMode={edit}
          />
        </Box>
      ),
      onConfirm: onCreateProductChange,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <Grid item>
        <Typography>{t('product.productChangeStateAll')}</Typography>
      </Grid>
      <Grid item>
        <Switch
          id="state"
          color="primary"
          onChange={handleFilterChange}
          checked={filterParam}
        />
      </Grid>
      <Grid item>
        <Typography>{t('product.productChangeStatePending')}</Typography>
      </Grid>
      <Button
        id="ProductChangesTab-createProductChange"
        variant="outlined"
        color="primary"
        startIcon={<Icon>assignment</Icon>}
        onClick={event => handleShowCreateChangeDialog({}, true)}
        disabled={actionsDisabled}
      >
        {t('product.createProductChange')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('common.empty')} />
    </GridOverlay>
  );

  return (
    <Box id="ProductChangesTab-container" className={classes.container}>
      <DataTable
        id="ProductChangesTab-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        onRowClick={handleShowCreateChangeDialog}
        sortModel={sortModel}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        onPageChange={onPageChange}
        pageSize={20}
        pageIndex={_.defaultTo(productChanges?.pageCount, 1) - 1}
        rowCount={_.defaultTo(productChanges?.totalCount, 0)}
        paginationMode="server"
        pagination
      />
    </Box>
  );
}

ProductChangesTab.propTypes = {
  product: PropTypes.object,
  productChanges: PropTypes.shape({
    productChanges: PropTypes.arrayOf(PropTypes.object).isRequired,
    pageCount: PropTypes.number,
    totalCount: PropTypes.number,
    pageEntries: PropTypes.number,
  }),
  actionsDisabled: PropTypes.bool.isRequired,
  filterParam: PropTypes.bool.isRequired,
  sortParams: PropTypes.object.isRequired,
  onCreateProductChange: PropTypes.func.isRequired,
  onCreateProductChangeError: PropTypes.func.isRequired,
  onDeleteProductChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default ProductChangesTab;
